import { useState } from 'react'

import { InternalRefetchQueriesInclude } from '@apollo/client'
import { Box, Button, Spinner } from '@factorialco/deprecated-design-system'
import {
  BulkAssignProjectClientListDocument,
  ClientsManageProjectDocument,
  ClientsManageProjectMutation,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

export interface BulkAssignProjectClientModalProps {
  projectIds: string[]
  onClose: () => void
  refetchQueries: InternalRefetchQueriesInclude
}

const BulkAssignProjectClientModal = ({
  projectIds,
  onClose,
  refetchQueries,
}: BulkAssignProjectClientModalProps) => {
  const { company } = useCurrentSession()
  const [clientId, setClientId] = useState<string | undefined>()

  const { loading, data } = useQuery(BulkAssignProjectClientListDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const [manage] = useMutation(ClientsManageProjectDocument, {
    extractorFn: (mutationData: ClientsManageProjectMutation) =>
      mutationData.projectManagementMutations.bulkClientAssignmentProjectmanagementProject,
    awaitRefetchQueries: true,
    refetchQueries,
  })

  const clients = data?.finance.contacts

  const onSubmitModal = async () => {
    const response = await manage({
      variables: {
        companyId: company.id,
        projectIds,
        clientId: String(clientId),
      },
    })

    const handled = handleResult(response, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('project_management.toasts.assign_client_multiple_projects_success'),
      })

      track(trackingEvents.projectManagement.bulkActions.clientAssignment.eventSubmit, {
        projectIds,
        clientId,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_saving'),
      })
    }
    onClose()
  }
  return (
    <Modal
      illustration={'add-user'}
      title={i18n.t('project_management.modals.bulk_assign_client.title')}
      description={i18n.t('project_management.modals.bulk_assign_client.description', {
        projects_number: projectIds.length,
      })}
      onClose={onClose}
      onSubmit={onSubmitModal}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={!clientId} submit>
            {i18n.t('common.add')}
          </Button>
        </>
      }
    >
      {loading ? (
        <Box width="full" paddingTop="s16" alignItems="center">
          <Spinner />
        </Box>
      ) : (
        <Box gap="s16">
          <Select
            label={i18n.t('project_management.modals.create_project.input_labels.client.label')}
            options={
              clients?.map((option) => ({
                label: option.name,
                value: option.id,
              })) ?? []
            }
            none={i18n.t('project_management.modals.create_project.input_labels.client.no_client')}
            defaultValue={clientId}
            onChange={(value) => setClientId(value)}
          />
        </Box>
      )}
    </Modal>
  )
}

export default observer(BulkAssignProjectClientModal)
